import { useContext, useEffect, useState } from "react";
import { APIContext } from "../../Contexts/APIContext";
import { SessionContext } from "../../Contexts/SessionContext";

import SiteFilterViewModel from "../../TripRate/ViewModels/SiteFilterViewModel";
import DropDownViewModel from "../../Utilities/ViewModels/DropDownViewModel";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CancelIcon from '@mui/icons-material/Cancel';
import SubLandUseViewModel from "../ViewModels/SubLandUseViewModel";

interface IDialogProps {
    open: boolean;
    onClose: () => void;
    setFilter: (filter: SiteFilterViewModel) => void;
}

export default function SiteFilterDialog({ open, onClose, setFilter }: IDialogProps) {
    const { get } = useContext(APIContext);
    const { sessionState } = useContext(SessionContext);
    const [locationDropdown, setLocationDropdown] = useState<DropDownViewModel[]>([]);
    const [subLocationDropdown, setSubLocationDropDown] = useState<DropDownViewModel[]>([]);
    const [useClassDropDown, setUseClassDropDown] = useState<DropDownViewModel[]>([]);
    const [areaValue, setAreaValue] = useState<SubLandUseViewModel>(new SubLandUseViewModel());
    //local filter allows value to be chosen before updating the table
    const [localFilter, setLocalFilter] = useState<SiteFilterViewModel>(new SiteFilterViewModel());

    //#region model methods
    //uses the name of the input to match the name of the model property
    const handleTextChange = (e: any) => {
        setLocalFilter({
            ...localFilter,
            [e.target.name]: e.target.value
        })
    }

    const handleNumberChange = (e: any) => {
        if (!isNaN(e.target.value))
            setLocalFilter({
                ...localFilter,
                [e.target.name]: e.target.value
            })
    }

    const handleTextClear = (property: string) => {
        setLocalFilter({
            ...localFilter,
            [property]: ''
        })
    }

    const handleCheckboxChange = () => {
        setLocalFilter({
            ...localFilter,
            showOnlySelected: !localFilter.showOnlySelected
        })
    }

    const applyChanges = () => {
        setFilter(localFilter);
        onClose();
    }
    //#endregion

    //#region db calls
    useEffect(() => {
        get('Site', `GetLocationDropDown`, setLocationDropdown);
        get('Site', `GetSubLocationDropDown`, setSubLocationDropDown);
        get('Site', `GetUseClassDropDown`, setUseClassDropDown);
        get('Site', `GetUniqueSubUseFields?mainLandUseId=${sessionState.tripRateMain}&subLandUseCode=${sessionState.tripRateSub}`, setAreaValue);
    }, []);
    //#endregion

    //#region error message handeling
    function isMinGreaterCheck(minVal: string, maxVal: string) {
        if (minVal !== '' && maxVal !== '') {//if both have a value
            if (+minVal > +maxVal) //can use the + string to num convert here as we know the input is only ver empty or number
                return true;
        }
        return false;
    }
    //#endregion

    return (
        <Dialog onClose={onClose} open={open} maxWidth="md" fullWidth>
            <DialogContent>
                <Grid container>
                    <Grid size={12}>
                        <Typography variant="h6" gutterBottom>Select Filters For Site Table</Typography>
                    </Grid>
                    <Grid size={6}> {/*e.g. gross floor*/}
                        <Typography variant="body1">{areaValue?.part1Description} From ({areaValue.park1Unit}) :</Typography>
                        <TextField
                            size="small"
                            name='variablePar1Min'
                            onChange={handleNumberChange}
                            value={localFilter.variablePar1Min}
                            error={isMinGreaterCheck(localFilter.variablePar1Min, localFilter.variablePar1Max)}
                            helperText={isMinGreaterCheck(localFilter.variablePar1Min, localFilter.variablePar1Max) ? "Min is greater than max" : ""}
                            placeholder={"Search By " + areaValue?.part1Description + " ..."}
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.variablePar1Min && (
                                    <IconButton
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('variablePar1Min')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                        />
                        <Typography variant="body1">{areaValue?.part1Description} To ({areaValue.park1Unit}) :</Typography>
                        <TextField
                            size="small"
                            name='variablePar1Max'
                            onChange={handleNumberChange}
                            value={localFilter.variablePar1Max}
                            error={isMinGreaterCheck(localFilter.variablePar1Min, localFilter.variablePar1Max)}
                            helperText={isMinGreaterCheck(localFilter.variablePar1Min, localFilter.variablePar1Max) ? "Max is smaller than max" : ""}
                            placeholder={"Search By " + areaValue?.part1Description + " ..."}
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.variablePar1Max && (
                                    <IconButton
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('variablePar1Max')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                        />
                    </Grid>
                    <Grid size={6}> {/*e.g. retail floor*/}
                        <Typography variant="body1">{areaValue.part2Description} From ({areaValue.park2Unit}) :</Typography>
                        <TextField
                            size="small"
                            name='variablePar2Min'
                            onChange={handleNumberChange}
                            value={localFilter.variablePar2Min}
                            error={isMinGreaterCheck(localFilter.variablePar2Min, localFilter.variablePar2Max)}
                            helperText={isMinGreaterCheck(localFilter.variablePar2Min, localFilter.variablePar2Max) ? "Min is greater than max" : ""}
                            placeholder={"Search By " + areaValue.part2Description + " ..."}
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.variablePar2Min && (
                                    <IconButton
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('variablePar2Min')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                        />
                        <Typography variant="body1">{areaValue.part2Description} To ({areaValue.park2Unit}) :</Typography>
                        <TextField
                            size="small"
                            name='variablePar2Max'
                            onChange={handleNumberChange}
                            value={localFilter.variablePar2Max}
                            error={isMinGreaterCheck(localFilter.variablePar2Min, localFilter.variablePar2Max)}
                            helperText={isMinGreaterCheck(localFilter.variablePar2Min, localFilter.variablePar2Max) ? "Min is greater than max" : ""}
                            placeholder={"Search By " + areaValue.part2Description + " ..."}
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.variablePar2Max && (
                                    <IconButton
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('variablePar2Max')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                        />
                    </Grid>
                    <Grid size={6}> {/*site floor*/}
                        <Typography variant="body1">Site Search :</Typography>
                        <TextField
                            size="small"
                            name='partialSiteName'
                            onChange={handleTextChange}
                            value={localFilter.partialSiteName}
                            placeholder="Search By Site..."
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.partialSiteName && (
                                    <IconButton
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('partialSiteName')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                        />
                    </Grid>
                    <Grid size={6}> {/*town floor*/}
                        <Typography variant="body1">Town Search :</Typography>
                        <TextField
                            size="small"
                            name='partialTownName'
                            onChange={handleTextChange}
                            value={localFilter.partialTownName}
                            placeholder="Search By Town..."
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.partialTownName && (
                                    <IconButton
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('partialTownName')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                        />
                    </Grid>
                    <Grid size={6}> {/*location floor*/}
                        <Typography variant="body1">Location Search :</Typography>
                        <TextField
                            size="small"
                            name='locationArea'
                            onChange={handleTextChange}
                            value={localFilter.locationArea}
                            select
                            placeholder="Search By Location..."
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.locationArea && (
                                    <IconButton
                                        sx={{ right: 10 }}
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => setLocalFilter({
                                            ...localFilter,
                                            locationSubArea: '',
                                            locationArea: ''
                                        })}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}>
                            {locationDropdown.map((location) =>
                                (<MenuItem key={location.label} value={location.label}>{`${location.label}`}</MenuItem>)
                            )}
                        </TextField>
                        <Typography variant="body1">Sub Location Search :</Typography>
                        <TextField
                            size="small"
                            name='locationSubArea'
                            onChange={handleTextChange}
                            value={localFilter.locationSubArea}
                            select
                            placeholder="Search By Sub Location..."
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.locationSubArea && (
                                    <IconButton
                                        sx={{ right: 10 }}
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('locationSubArea')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}>
                            {subLocationDropdown.map((location) =>
                                (<MenuItem key={location.label} value={location.label}>{`${location.label}`}</MenuItem>)
                            )}
                        </TextField>
                    </Grid>
                    <Grid size={6}> {/*parking floor*/}
                        <Typography variant="body1">Parking Spaces From :</Typography>
                        <TextField
                            size="small"
                            name='parkingAreaMin'
                            onChange={handleNumberChange}
                            value={localFilter.parkingAreaMin}
                            error={isMinGreaterCheck(localFilter.parkingAreaMin, localFilter.parkingAreaMax)}
                            helperText={isMinGreaterCheck(localFilter.parkingAreaMin, localFilter.parkingAreaMax) ? "Min is greater than max" : ""}
                            placeholder="Spaces From..."
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.parkingAreaMin && (
                                    <IconButton
                                        sx={{ right: 10 }}
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('parkingAreaMin')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }} />
                        <Typography variant="body1">Parking Spaces To :</Typography>
                        <TextField
                            size="small"
                            name='parkingAreaMax'
                            onChange={handleNumberChange}
                            value={localFilter.parkingAreaMax}
                            error={isMinGreaterCheck(localFilter.parkingAreaMin, localFilter.parkingAreaMax)}
                            helperText={isMinGreaterCheck(localFilter.parkingAreaMin, localFilter.parkingAreaMax) ? "Min is greater than max" : ""}
                            placeholder="Spaces To..."
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.parkingAreaMax && (
                                    <IconButton
                                        sx={{ right: 10 }}
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('parkingAreaMax')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }} />
                    </Grid>
                    <Grid size={6}> {/*use class floor*/}
                        <Typography variant="body1">Use Class :</Typography>
                        <TextField
                            size="small"
                            name='useClass'
                            onChange={handleTextChange}
                            value={localFilter.useClass}
                            select
                            placeholder="Search By Use Class..."
                            fullWidth
                            InputProps={{
                                endAdornment: localFilter.useClass && (
                                    <IconButton
                                        sx={{ right: 10 }}
                                        size="small"
                                        aria-label="clear value"
                                        onClick={() => handleTextClear('useClass')}
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}>
                            {useClassDropDown.map((location) =>
                                (<MenuItem key={location.label} value={location.id.toString()}>{`${location.label}`}</MenuItem>)
                            )}
                        </TextField>
                    </Grid>
                    <Grid size={6} />
                    <Grid size={5} />
                    <Grid size={5}>
                        <Button sx={{ verticalAlign: 'text-bottom' }} onClick={handleCheckboxChange} variant="contained">
                            <Checkbox checked={localFilter.showOnlySelected} color="default" style={{ padding: 0, paddingRight: 9 }} /> Show Only Currently Selected
                        </Button>
                    </Grid>
                    <Grid size={2}>
                        <Button sx={{ verticalAlign: 'text-bottom' }} onClick={applyChanges} variant="contained">Apply</Button>
                    </Grid>
                </Grid >
            </DialogContent>
        </Dialog >
    );
}